import moment, { Moment } from 'moment';
import { IndividualAdditionalRole, SavePartyChangedField, SignerRoleTypes } from './application-service.domain';
import { ApplicationIndividual } from './application-parties.domain';
import { individualSignerRole } from '../utils/application-party-labels';
import { Address2ComponentValue } from './component/address2-component.domain';
import { countryOfCitizenshipByName, IndividualFormValue } from './component/individual-form-component.domain';
import { TitleSelectionValueOptions } from './component/title-selection-component.domain';
import { parseRawAddress } from '../utils/address';

/** The current KYC-tab form state for one individual: the nested <individual-form> value plus the KYC-specific controls. */
export interface KycIndividualFormState {
  individual: IndividualFormValue;
  signerRole: SignerRoleTypes;
  contact: string | null;
  // Partial because FormArray.value reports partial rows; the field is only serialised for comparison
  additionalRoles: Partial<IndividualAdditionalRole>[];
}

export type KycIndividualChangeMap = { [key: string]: { original: any; current: any } };

// Human labels for the change-map keys, used by the per-individual audit record in the
// save-parties flow. Kept next to kycIndividualChanges so a newly-compared field gets its label here.
export const KYC_INDIVIDUAL_FIELD_LABELS: { [key: string]: string } = {
  title: 'Title',
  firstName: 'First name',
  middleName: 'Middle name',
  lastName: 'Last name',
  dob: 'Date of birth',
  residentialAddress: 'Residential address',
  email: 'Email',
  mobile: 'Mobile number',
  hasAlias: 'Has alias',
  aliasNames: 'Alias names',
  countryOfCitizenship: 'Country of citizenship',
  identification: 'Identification',
  idDocument: 'ID document',
  signerRole: 'Signer role',
  contact: 'Primary contact',
  additionalRoles: 'Additional roles',
};

export const kycChangedFieldLabel = (key: string): string => KYC_INDIVIDUAL_FIELD_LABELS[key] ?? key;

// Display-ready old/new value for the audit table columns. Booleans read as Yes/No; the JSON-diffed
// additionalRoles summarize as "role @ entity"; a pending ID document lists its file names.
const kycChangeValueDisplay = (key: string, value: any): string => {
  if (key === 'hasAlias') {
    return value === true || value === 'true' ? 'Yes' : 'No';
  }
  if (key === 'additionalRoles') {
    try {
      const roles = JSON.parse(value || '[]') as IndividualAdditionalRole[];
      if (!Array.isArray(roles) || !roles.length) {
        return 'None';
      }
      return roles.map(r => `${r.signerRole} @ ${r.business?.name ?? r.business?.abn ?? ''}`.trim()).join('; ');
    } catch {
      return String(value ?? '');
    }
  }
  return String(value ?? '');
};

// The diff as one audit-ready entry per changed field, consumed by the save-parties payload so the
// server can write one audit row per field with Old Value / New Value filled in.
export const kycChangedFieldEntries = (changes: KycIndividualChangeMap): SavePartyChangedField[] =>
  Object.entries(changes).map(([key, { original, current }]) => ({
    field: key,
    oldValue: kycChangeValueDisplay(key, original),
    newValue: kycChangeValueDisplay(key, current),
  }));

// The title options are Prof/Dr/Mr/Ms/Mrs, so a stored title outside them ('Dr.', 'Miss', ...) loads
// into the form as null. The stored value must pass through the same lookup before diffing, otherwise
// every such record reads as permanently edited.
const formRepresentableTitleType = (title?: string | null): string =>
  TitleSelectionValueOptions.find(o => o.type.toLowerCase() === (title ?? '').toString().toLowerCase())?.type ?? '';

const dobText = (d?: Moment | null): string => (d && d.isValid() ? d.format('YYYY-MM-DD') : '');

// Parts-only comparison, same as the KYB company-details diff: custom-address derives the `address`
// line from the parts and formats it its own way, so the line can never be diffed against the stored
// UnformattedAddress without reporting an edit on every record.
const addressParts = (value: Address2ComponentValue): string =>
  [
    value?.UnitNumber,
    value?.StreetNumber,
    value?.StreetName,
    value?.StreetType,
    value?.Suburb,
    value?.State,
    value?.Postcode,
  ]
    .map(part => (part ?? '').trim().toUpperCase())
    .join('|');

// The address the nested form loads for a stored record (mirrors the portal's
// individualResidentialAddress, including its parsed-parts fallbacks).
const loadedResidentialAddress = (initial: ApplicationIndividual): Address2ComponentValue => {
  const home = initial.homeAddress;
  if (!home) {
    return null;
  }
  const parsed = parseRawAddress(home.unformattedAddress ?? '');
  return {
    address: home.unformattedAddress ?? '',
    UnitNumber: parsed?.unit_number ?? '',
    StreetNumber: home.streetNumber ? home.streetNumber : (parsed?.street_number ?? ''),
    StreetName: home.streetName ? home.streetName : (parsed?.street_name ?? ''),
    StreetType: home.streetType ? home.streetType : (parsed?.street_type ?? ''),
    Suburb: home.suburb ? home.suburb : (parsed?.locality ?? ''),
    State: home.state ? home.state : (parsed?.region ?? ''),
    Postcode: home.postcode ? home.postcode : (parsed?.postal_code ?? ''),
  };
};

const EMPTY_PARTS = addressParts(null);

const residentialAddressChanged = (initial: ApplicationIndividual, current: Address2ComponentValue): boolean => {
  const loaded = loadedResidentialAddress(initial);
  const loadedParts = addressParts(loaded);
  const currentParts = addressParts(current);
  if (loadedParts !== currentParts) {
    return true;
  }
  // Neither side parsed into parts (free-text address): the parts diff is blind, so fall back to
  // the raw line, which the control cannot reformat without parts.
  if (loadedParts === EMPTY_PARTS) {
    return (loaded?.address ?? '').trim() !== (current?.address ?? '').trim();
  }
  return false;
};

/**
 * Diffs the KYC-tab form state of one individual against the loaded record. Change keys keep the
 * historical flat names (firstName, dob, ...) because the consent / re-verification critical lists
 * and the confirmation dialog are keyed on them.
 *
 * The loaded side of each comparison is normalised through the same mapping the form applies on
 * load (title option lookup, country option lookup, moment parse, address parts). Without that, a
 * record whose stored value the form cannot represent exactly reads as permanently "changed" - and
 * because such legacy records are also invalid (the required title/citizenship control is null), the
 * section-level Save stays disabled no matter which individual was actually edited.
 */
export const kycIndividualChanges = (
  initial: ApplicationIndividual,
  state: KycIndividualFormState,
  /**
   * The loaded corporate-guarantor rows, in the same shape the form control holds. Passed in rather
   * than read off `initial`: on the API shape these are the non-primary roles, which carry only
   * entityId and entityLegalName, while the form value needs the entity's ABN/ACN (AZ-1475).
   */
  initialAdditionalRoles: IndividualAdditionalRole[] = [],
): KycIndividualChangeMap => {
  const changes: KycIndividualChangeMap = {};
  const v = state.individual;

  const compare = (key: string, original: any, current: any) => {
    const o = original ?? '';
    const c = current ?? '';
    if (o !== c) {
      changes[key] = { original: o, current: c };
    }
  };

  compare('title', formRepresentableTitleType(initial.title ?? ''), v?.title?.type);
  compare('firstName', initial.givenName, v?.firstName);
  compare('middleName', initial.middleName, v?.middleName);
  compare('lastName', initial.surname, v?.lastName);
  compare('dob', dobText(moment(initial.dateOfBirth, 'YYYY-MM-DD')), dobText(v?.dob));
  if (residentialAddressChanged(initial, v?.residentialAddress ?? null)) {
    changes['residentialAddress'] = {
      original: initial.homeAddress?.unformattedAddress ?? '',
      current: v?.residentialAddress?.address ?? '',
    };
  }
  compare('email', initial.email, v?.email);
  compare('mobile', initial.mobileNumber, v?.mobileNumber);
  compare('hasAlias', !!initial.hasAlias, !!v?.hasAlias);
  // The form only carries alias names when hasAlias is ticked; a stored alias on an unticked record
  // loads as null, so the stored side is normalised the same way before diffing.
  compare('aliasNames', initial.hasAlias ? initial.aliasNames : null, v?.hasAlias ? v?.aliasNames : null);
  compare('countryOfCitizenship', countryOfCitizenshipByName(initial.countryOfCitizenship ?? '')?.name, v?.countryOfCitizenship?.name);
  // both sides normalised to "type number" only when complete, mirroring the form's initial value
  compare('identification',
    (initial.identification?.type && initial.identification?.number) ? `${initial.identification.type} ${initial.identification.number}` : '',
    (v?.identification?.idType && v?.identification?.idNumber) ? `${v.identification.idType} ${v.identification.idNumber}` : '');
  // pending in-memory ID document files count as a change so the section Save can upload them
  compare('idDocument', '', (v?.idDocument ?? []).map(f => f.name).join(','));
  compare('signerRole', individualSignerRole(initial), state.signerRole);
  compare('contact', initial.isPrimaryContact ? 'primaryContact' : 'contact', state.contact);
  compare('additionalRoles',
    JSON.stringify(initialAdditionalRoles),
    JSON.stringify(state.additionalRoles ?? []));

  return changes;
};
